import { logger } from '../lib/logger';

export type CaptureLockPurpose = 'recording' | 'recovery';

// Recording keeps the original name, so an old bundle in another tab still sees
// a new bundle's recording and vice versa. Recovery takes its own name: the
// drain keeps out of a live recording's rows by recency, not by this lock.
const LOCK_NAMES: Record<CaptureLockPurpose, string> = {
  recording: 'heidi-session',
  recovery: 'heidi-session-recovery',
};

export interface CaptureLockToken {
  readonly id: symbol;
  readonly purpose: CaptureLockPurpose;
}

export type CaptureLockResult =
  | { status: 'acquired' | 'unsupported'; token: CaptureLockToken }
  | { status: 'unavailable' };

interface HeldLock {
  token: CaptureLockToken;
  releaseBrowserLock: (() => void) | null;
}

// One lease per purpose, so this tab can record and drain at the same time.
const heldLocks = new Map<CaptureLockPurpose, HeldLock>();
// The one lease a start may take over. A hold is not adoptable by virtue of
// its purpose — a start still in async prep holds a 'recording' lease too.
let adoptableTailToken: CaptureLockToken | null = null;

// Single drop point, so no path strands the browser lock behind a cleared lease.
const clearHeldLock = (purpose: CaptureLockPurpose): void => {
  const held = heldLocks.get(purpose);
  held?.releaseBrowserLock?.();
  if (held?.token === adoptableTailToken) adoptableTailToken = null;
  heldLocks.delete(purpose);
};

const reserveLocalLock = (purpose: CaptureLockPurpose): CaptureLockToken => {
  const token = { id: Symbol(purpose), purpose };
  heldLocks.set(purpose, { token, releaseBrowserLock: null });
  return token;
};

const holdsLease = (purpose: CaptureLockPurpose, token: CaptureLockToken): boolean =>
  heldLocks.get(purpose)?.token === token;

/** Acquires a non-queuing origin-wide lock plus an always-on same-tab lease. */
export const tryAcquireCaptureLock = (
  purpose: CaptureLockPurpose
): Promise<CaptureLockResult> => {
  if (heldLocks.has(purpose)) return Promise.resolve({ status: 'unavailable' });

  const token = reserveLocalLock(purpose);
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.locks?.request !== 'function'
  ) {
    logger.info({ event_type: 'CAPTURE_LOCK_UNSUPPORTED', details: { purpose } });
    return Promise.resolve({ status: 'unsupported', token });
  }

  return new Promise<CaptureLockResult>((resolve) => {
    try {
      navigator.locks
        .request(LOCK_NAMES[purpose], { ifAvailable: true }, (lock) => {
          if (!lock) {
            if (holdsLease(purpose, token)) clearHeldLock(purpose);
            resolve({ status: 'unavailable' });
            return;
          }
          return new Promise<void>((release) => {
            // The local reservation was released while acquisition was in
            // flight (e.g. logout teardown) — surrender the browser lock and
            // settle, or the awaiting start() hangs forever.
            const held = heldLocks.get(purpose);
            if (held?.token !== token) {
              release();
              resolve({ status: 'unavailable' });
              return;
            }
            held.releaseBrowserLock = release;
            logger.info({ event_type: 'CAPTURE_LOCK_ACQUIRED', details: { purpose } });
            resolve({ status: 'acquired', token });
          });
        })
        .catch((err) => {
          if (holdsLease(purpose, token)) {
            logger.warn({
              event_type: 'CAPTURE_LOCK_ERROR',
              details: {
                purpose,
                error: err instanceof Error ? err.message : String(err),
              },
            });
            resolve({ status: 'unsupported', token });
          } else {
            resolve({ status: 'unavailable' });
          }
        });
    } catch (err) {
      logger.warn({
        event_type: 'CAPTURE_LOCK_ERROR',
        details: { purpose, error: err instanceof Error ? err.message : String(err) },
      });
      resolve({ status: 'unsupported', token });
    }
  });
};

/** Whether this tab holds its own lease for `purpose`. */
export const hasLocalCaptureLease = (purpose: CaptureLockPurpose): boolean =>
  heldLocks.has(purpose);

/** Whether this tab holds any lease at all, whatever its purpose. */
export const hasAnyLocalCaptureLease = (): boolean => heldLocks.size > 0;

/** Releases only the exact lease returned to this owner. */
export const releaseCaptureLock = (token: CaptureLockToken): boolean => {
  if (!holdsLease(token.purpose, token)) return false;
  clearHeldLock(token.purpose);
  return true;
};

/**
 * Marks this lease as riding a finished recording's upload drain, which is the
 * only state `adoptCaptureLock` will hand over. Ignored for a stale token.
 */
export const markAdoptableTail = (token: CaptureLockToken): void => {
  if (!holdsLease(token.purpose, token)) return;
  adoptableTailToken = token;
};

/**
 * Hands a draining tail's lease to a new owner without ever dropping it — the
 * lease only keeps hydration off pending uploads, which the incoming recording
 * does equally well. Synchronous, so no start slips between check and swap.
 */
export const adoptCaptureLock = (): CaptureLockToken | null => {
  const held = heldLocks.get('recording');
  if (!held || held.token !== adoptableTailToken) return null;
  const token: CaptureLockToken = { id: Symbol('recording'), purpose: 'recording' };
  held.token = token;
  adoptableTailToken = null;
  return token;
};

/** Test-only. */
export const __resetCaptureLockForTests = (): void => {
  heldLocks.forEach((held) => held.releaseBrowserLock?.());
  heldLocks.clear();
  adoptableTailToken = null;
};
